import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DocumentMagnifyingGlassIcon,
  ClockIcon,
  TruckIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  ClipboardDocumentCheckIcon,
  InformationCircleIcon,
  CalendarDaysIcon,
  UserCircleIcon,
  HandRaisedIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';

// Import API functions
import { 
  offboardingAPI,
  getOffboardingStatus,
  submitResignation,
  submitHandover,
  confirmAssetReturn,
  submitFinalDocs
} from '../services/employee.service';

// Import fix utilities
import { fixResignationStatus } from '../utils/fixResignationStatus';

// Handle Leaflet errors
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (event.error && event.error.message && event.error.message.includes('_leaflet_pos')) {
      console.warn('Leaflet error handled:', event.error.message);
      event.preventDefault();
      return false;
    }
  });
}

// --- CONFIGURATION & MOCK DATA ---
const EMPLOYEE_MOCK_DATA = {
  joiningDate: '2022-05-15',
  managerName: 'Jane Doe',
  companyAssets: [
    { id: 1, name: 'Laptop (MacBook Pro 2023)', serial: 'LAP-12345', status: 'Pending Return' },
    { id: 2, name: 'Company Mobile Phone (iPhone 14)', serial: 'PHN-67890', status: 'Pending Return' },
    { id: 3, name: 'Access Card', serial: 'ACC-11223', status: 'Pending Return' },
  ]
};

const OFFBOARDING_STEPS = [
  { id: 1, name: 'Resignation Submission', icon: HandRaisedIcon },
  { id: 2, name: 'Notice Period & Exit Date', icon: ClockIcon },
  { id: 3, name: 'Knowledge/Task Handover', icon: DocumentMagnifyingGlassIcon },
  { id: 4, name: 'Asset Return Confirmation', icon: TruckIcon },
  { id: 5, name: 'Final Documentation', icon: CheckCircleIcon },
];

// --- UTILS ---
const calculateExitDates = (resignationDate, noticePeriodDays) => {
  if (!resignationDate) return { lastWorkingDay: null, effectiveReliefDate: null };

  const date = new Date(resignationDate);
  const lastWorkingDay = new Date(date);
  lastWorkingDay.setDate(date.getDate() + noticePeriodDays);

  const effectiveReliefDate = new Date(lastWorkingDay);
  effectiveReliefDate.setDate(lastWorkingDay.getDate() + 1);

  const formatDate = (d) => d.toISOString().split('T')[0];

  return {
    lastWorkingDay: formatDate(lastWorkingDay),
    effectiveReliefDate: formatDate(effectiveReliefDate)
  };
};

const daysBetween = (date1, date2) => {
  if (!date1 || !date2) return 0;
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// --- COMPONENTS ---

const FormInput = ({ id, label, type = 'text', value, onChange, placeholder, required = true, rows = 1, disabled = false, children }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-semibold text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {type === 'textarea' ? (
      <textarea
        id={id}
        name={id}
        rows={rows}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className={`w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:ring-red-500 focus:border-red-500 text-sm transition font-roboto ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : ''
        }`}
      />
    ) : type === 'select' ? (
      <select
        id={id}
        name={id}
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={`w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm text-sm font-medium transition font-roboto ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'focus:ring-red-500 focus:border-red-500'
        }`}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {children}
      </select>
    ) : (
      <input
        type={type}
        id={id}
        name={id}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className={`w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm text-sm transition font-roboto ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'focus:ring-red-500 focus:border-red-500'
        }`}
      />
    )}
  </div>
);

const AssetStatusTag = ({ status }) => {
  let statusClasses = '';
  if (status === 'Pending Return') statusClasses = 'bg-yellow-100 text-yellow-700 border-yellow-300';
  else if (status === 'Confirmed') statusClasses = 'bg-green-100 text-green-700 border-green-300';
  else statusClasses = 'bg-gray-100 text-gray-700 border-gray-300';
  
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${statusClasses} font-roboto tracking-wider`}>
      {status}
    </span>
  );
};

// --- MAIN COMPONENT ---
const EmployeeOffboarding = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    resignationDate: '',
    resignationReason: '',
    noticePeriodDays: 60,
    requestedLastWorkingDay: '',
    managerNotes: '',
    handoverDocumentLink: '',
    handoverRecipient: '',
    pendingTasks: '',
    assetReturnNotes: '',
    documentDeliveryMethod: '',
  });

  const [isResignationSubmitted, setIsResignationSubmitted] = useState(false);
  const [isManagerApproved, setIsManagerApproved] = useState(false);
  const [companyAssets, setCompanyAssets] = useState([]);
  const [completionMessage, setCompletionMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [employeeData, setEmployeeData] = useState({
    joiningDate: '2022-05-15',
    managerName: 'Jane Doe'
  });

  const totalSteps = OFFBOARDING_STEPS.length;
  const CurrentStepIcon = OFFBOARDING_STEPS.find(s => s.id === currentStep)?.icon;

  // Test API connectivity
  const testAPIConnection = async () => {
    try {
      console.log('🧪 Testing API connection...');
      const response = await fetch('https://hrms.gtplindia.com:8443/offboarding/test');
      const data = await response.json();
      console.log('✅ API test successful:', data);
      return true;
    } catch (error) {
      console.error('❌ API test failed:', error);
      return false;
    }
  };

  // Load offboarding status on component mount
  useEffect(() => {
    const initializeComponent = async () => {
      const apiWorking = await testAPIConnection();
      if (apiWorking) {
        loadOffboardingStatus();
      } else {
        console.error('❌ API not accessible, using mock data');
        setCompanyAssets(EMPLOYEE_MOCK_DATA.companyAssets);
        setLoading(false);
      }
    };
    
    initializeComponent();
    
    // Poll for status updates every 30 seconds
    const interval = setInterval(() => {
      if (!loading) {
        loadOffboardingStatus();
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const loadOffboardingStatus = async () => {
    try {
      setLoading(true);
      console.log('🔄 Loading offboarding status...');
      
      const response = await getOffboardingStatus();
      console.log('✅ Offboarding status response:', response);
      
      const data = response?.data || response;
      console.log('📊 Offboarding data:', data);
      
      // Check if data exists and has the expected structure
      if (data && typeof data === 'object') {
        if (data.has_active_resignation && data.resignation_data) {
          const resignation = data.resignation_data;
          console.log('📋 Found active resignation:', resignation);
          
          setFormData(prev => ({
            ...prev,
            resignationDate: resignation.resignation_date?.split('T')[0] || '',
            resignationReason: resignation.reason || '',
            noticePeriodDays: resignation.notice_period_days || 60,
            requestedLastWorkingDay: resignation.requested_lwd?.split('T')[0] || ''
          }));
          setIsResignationSubmitted(true);
          
          // Handle different resignation statuses
          if (resignation.status === 'manager_approved') {
            setIsManagerApproved(true);
          } else if (resignation.status === 'pending_final_manager_approval') {
            setIsManagerApproved(true);
            // Show message that final submission is with manager
            setCompletionMessage('✅ Your resignation has been submitted for final manager approval. Please wait for your manager to review the completed documentation.');
          } else if (resignation.status === 'pending_hr_approval') {
            setIsManagerApproved(true);
            // Show message that it's with HR
            setCompletionMessage('✅ Your resignation has been approved by your manager and is now with HR for final processing. You will receive your documents once HR completes the review.');
          } else if (resignation.status === 'hr_approved') {
            setIsManagerApproved(true);
            // Show final completion message
            setCompletionMessage('🎉 Your resignation has been fully approved! HR will send your relieving letter and experience certificate to your registered email address.');
            setCurrentStep(totalSteps + 1);
          } else if (resignation.status === 'hr_rejected') {
            setIsManagerApproved(false); // Reset manager approval to allow re-submission
            setIsResignationSubmitted(false); // Allow re-submission
            setCurrentStep(1); // Reset to step 1
            // Show rejection message with reason
            setCompletionMessage(`❌ Your resignation has been rejected by HR. Reason: ${resignation.rejection_reason || 'Please contact HR for details.'}\n\nYou can now modify your resignation details and resubmit.`);
          } else if (resignation.status === 'manager_rejected') {
            setIsManagerApproved(false); // Reset manager approval
            setIsResignationSubmitted(false); // Allow re-submission
            setCurrentStep(1); // Reset to step 1
            // Show rejection message with reason
            setCompletionMessage(`❌ Your resignation has been rejected by your manager. Reason: ${resignation.manager_reason || 'Please contact your manager for details.'}\n\nYou can now modify your resignation details and resubmit.`);
          } else {
            setIsManagerApproved(resignation.manager_approved || false);
          }
        }
        
        setCompanyAssets(data.company_assets || EMPLOYEE_MOCK_DATA.companyAssets);
        setEmployeeData({
          joiningDate: data.joining_date || '2022-05-15',
          managerName: data.manager_name || 'Jane Doe'
        });
        
        console.log('✅ Offboarding status loaded successfully');
      } else {
        console.warn('⚠️ Invalid data structure, using fallback');
        setCompanyAssets(EMPLOYEE_MOCK_DATA.companyAssets);
      }
    } catch (error) {
      console.error('❌ Failed to load offboarding status:', error);
      setCompanyAssets(EMPLOYEE_MOCK_DATA.companyAssets);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAssetStatusChange = (assetId, newStatus) => {
    setCompanyAssets(prev => prev.map(asset => 
      asset.id === assetId ? { ...asset, status: newStatus } : asset
    ));
  };

  const handleSubmitResignation = async () => {
    if (formData.resignationDate && formData.resignationReason) {
      try {
        setLoading(true);
        
        // Calculate the last working day
        const calculatedDates = calculateExitDates(formData.resignationDate, formData.noticePeriodDays);
        const lastWorkingDay = calculatedDates.lastWorkingDay;
        
        const resignationData = {
          resignation_date: formData.resignationDate,
          reason: formData.resignationReason,
          notice_period_days: formData.noticePeriodDays,
          requested_lwd: lastWorkingDay
        };
        
        console.log('Submitting resignation data:', resignationData);
        
        const response = await submitResignation(resignationData);
        
        if (response.data) {
          setIsResignationSubmitted(true);
          setIsManagerApproved(false);
          
          // Update form data with calculated LWD
          setFormData(prev => ({
            ...prev,
            requestedLastWorkingDay: lastWorkingDay
          }));
          
          alert('Resignation submitted successfully! Your manager will be notified for approval.');
        }
      } catch (error) {
        console.error('Failed to submit resignation:', error);
        alert('Failed to submit resignation. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      alert('Please fill in both the resignation date and reason to proceed.');
    }
  };

  const handleFinalCompletion = async (e) => {
    e.preventDefault();

    const allAssetsConfirmed = companyAssets.every(asset => asset.status === 'Confirmed');
    if (!allAssetsConfirmed) {
      alert("All company assets must be confirmed for return or clearance to finalize the exit process.");
      return;
    }
    if (!formData.documentDeliveryMethod) {
      alert("Please specify your preferred document delivery method.");
      return;
    }
    
    try {
      setLoading(true);
      const empCode = localStorage.getItem('emp_code');
      
      const docsData = {
        personal_email: formData.documentDeliveryMethod,
        final_submission: true,
        assets_confirmed: true,
        handover_completed: true,
        status_update: 'pending_hr_approval'
      };
      
      console.log('Submitting final documentation with HR status update:', docsData);
      const response = await submitFinalDocs(docsData);
      console.log('Final docs response:', response);
      
      // Try to fix status if needed
      try {
        console.log('🔧 Attempting to fix resignation status...');
        await fixResignationStatus(empCode);
        console.log('✅ Status fix attempted');
      } catch (fixError) {
        console.warn('Status fix failed, continuing:', fixError);
      }
      
      await loadOffboardingStatus();
      
      setCompletionMessage(`✅ Exit process completed successfully! Your resignation has been forwarded to HR for final approval. You will receive your Relieving and Experience letters via ${formData.documentDeliveryMethod} upon HR clearance. Last Working Day: ${formData.requestedLastWorkingDay}`);
      setCurrentStep(totalSteps + 1);
      
      alert('🎉 Exit process submitted successfully! Your complete resignation package has been forwarded to HR for final processing.');
      
    } catch (error) {
      console.error('Failed to complete final documentation:', error);
      
      try {
        const empCode = localStorage.getItem('emp_code');
        await fixResignationStatus(empCode);
        await loadOffboardingStatus();
        
        setCompletionMessage(`✅ Exit process completed! Your resignation documentation has been forwarded to HR for final approval.`);
        setCurrentStep(totalSteps + 1);
        alert('✅ Exit process completed! Your documentation has been forwarded to HR for processing.');
        
      } catch (fallbackError) {
        setCompletionMessage(`✅ Exit process completed! Please contact HR if you don't receive updates within 24 hours.`);
        setCurrentStep(totalSteps + 1);
        alert('✅ Exit process completed! Please contact HR for status updates.');
      }
    } finally {
      setLoading(false);
    }
  };

  const isStepValid = useMemo(() => {
    switch (currentStep) {
      case 1: return true; // Always allow step 1
      case 2: 
        // Step 2 is locked until resignation is submitted and manager approved
        // If manager rejected, unlock step 1 and 2 for re-submission
        if (completionMessage && completionMessage.includes('rejected')) {
          return true; // Allow editing if rejected
        }
        return isResignationSubmitted && isManagerApproved;
      case 3: 
        // Step 3 requires manager approval and no rejection
        if (completionMessage && completionMessage.includes('rejected')) {
          return false; // Lock if rejected
        }
        return isResignationSubmitted && isManagerApproved && !!formData.handoverDocumentLink && !!formData.handoverRecipient && !!formData.pendingTasks;
      case 4: 
        // Step 4 requires handover completion and no rejection
        if (completionMessage && completionMessage.includes('rejected')) {
          return false; // Lock if rejected
        }
        return isResignationSubmitted && isManagerApproved && !!formData.handoverDocumentLink;
      case 5: 
        // Step 5 requires all assets cleared and no rejection
        if (completionMessage && completionMessage.includes('rejected')) {
          return false; // Lock if rejected
        }
        const allAssetsCleared = companyAssets.every(asset => asset.status === 'Confirmed');
        return isResignationSubmitted && isManagerApproved && allAssetsCleared && !!formData.documentDeliveryMethod;
      default: return true;
    }
  }, [currentStep, formData, isResignationSubmitted, isManagerApproved, companyAssets, completionMessage]);

  const handleNextStep = (e) => {
    e.preventDefault();
    
    // Step 1: Allow navigation only after form is filled
    if (currentStep === 1) {
      if (!formData.resignationDate || !formData.resignationReason) {
        alert('Please fill in both the resignation date and reason before proceeding.');
        return;
      }
      setCurrentStep(2);
      return;
    }
    
    // Step 2: Must submit resignation and wait for manager approval
    if (currentStep === 2) {
      if (!isResignationSubmitted) {
        handleSubmitResignation();
        return;
      }
      if (!isManagerApproved) {
        alert('Please wait for manager approval before proceeding to the next step.');
        return;
      }
      setCurrentStep(3);
      return;
    }
    
    // Step 3: Must complete handover
    if (currentStep === 3) {
      if (!formData.handoverDocumentLink || !formData.handoverRecipient || !formData.pendingTasks) {
        alert('Please complete all handover fields before proceeding.');
        return;
      }
      handleSubmitHandover();
      return;
    }
    
    // Step 4: Must confirm all assets
    if (currentStep === 4) {
      const allAssetsConfirmed = companyAssets.every(asset => asset.status === 'Confirmed');
      if (!allAssetsConfirmed) {
        alert('Please confirm the return status of all company assets before proceeding.');
        return;
      }
      handleSubmitAssets();
      return;
    }
    
    // Step 5: Final submission handled separately
    if (currentStep === 5) {
      // This will be handled by the final submit button
      return;
    }
    
    // Default case
    if (isStepValid) {
      setCurrentStep(prev => prev + 1);
    } else {
      alert('Please complete all required fields and actions in this step before moving forward.');
    }
  };

  const handleSubmitHandover = async () => {
    try {
      setLoading(true);
      const handoverData = {
        handover_link: formData.handoverDocumentLink,
        handover_to: formData.handoverRecipient,
        pending_tasks: formData.pendingTasks
      };
      
      await submitHandover(handoverData);
      setCurrentStep(prev => prev + 1);
      alert('Handover documentation submitted successfully!');
    } catch (error) {
      console.error('Failed to submit handover:', error);
      alert('Failed to submit handover documentation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAssets = async () => {
    try {
      setLoading(true);
      const assetData = {
        assets_confirmed: companyAssets.every(asset => asset.status === 'Confirmed'),
        asset_notes: formData.assetReturnNotes
      };
      
      await confirmAssetReturn(assetData);
      setCurrentStep(prev => prev + 1);
      alert('Asset return status confirmed!');
    } catch (error) {
      console.error('Failed to confirm assets:', error);
      alert('Failed to confirm asset return. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && currentStep === 1) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading offboarding status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-roboto">
      <div className="max-w-6xl mx-auto py-10 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 p-6 bg-white rounded-2xl shadow-xl border border-red-100"
        >
          <h1 className="text-3xl font-extrabold text-gray-900 font-poppins">
            Employee Exit & Offboarding Wizard
          </h1>
          <p className="mt-1 text-gray-500 text-sm font-roboto">
            A structured, fully compliant process to ensure all required formalities are completed smoothly.
          </p>
        </motion.div>

        {/* Multi-Step Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          
          {currentStep <= OFFBOARDING_STEPS.length && (
            <div className="flex justify-between items-center py-4 px-8 bg-white border-b border-gray-100 rounded-t-2xl shadow-sm">
              {OFFBOARDING_STEPS.map((step) => {
                const isStepLocked = () => {
                  if (completionMessage && completionMessage.includes('rejected')) {
                    // If rejected, only allow steps 1 and 2 for re-submission
                    return step.id > 2;
                  }
                  // Normal locking logic
                  if (step.id === 1) return false; // Step 1 always unlocked
                  if (step.id === 2) return !isResignationSubmitted;
                  if (step.id > 2) return !isManagerApproved;
                  return false;
                };
                
                return (
                  <div key={step.id} className="relative flex items-center group">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-8 h-8 flex items-center justify-center rounded-full border-2 transition-all duration-300 z-10 relative
                          ${currentStep > step.id ? 'bg-green-600 border-green-600' : 
                            currentStep === step.id ? 'bg-red-500 border-red-500' : 
                            isStepLocked() ? 'bg-gray-200 border-gray-300' :
                            'bg-white border-gray-300'
                          }`}
                      >
                        {isStepLocked() && (
                          <svg className="w-3 h-3 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                          </svg>
                        )}
                        {!isStepLocked() && (
                          currentStep > step.id ? (
                            <CheckCircleIcon className="h-4 w-4 text-white" />
                          ) : (
                            <step.icon className={`h-4 w-4 ${currentStep === step.id ? 'text-white' : 'text-gray-400'}`} />
                          )
                        )}
                      </div>
                      <p className={`mt-2 text-xs font-medium text-center transition-colors duration-300 ${
                        currentStep === step.id ? 'text-red-600 font-bold' : 
                        isStepLocked() ? 'text-gray-400' :
                        'text-gray-500'
                      }`}>
                        {step.name}
                        {isStepLocked() && <span className="block text-[10px] text-red-500">Locked</span>}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Form Content */}
          {currentStep <= OFFBOARDING_STEPS.length ? (
            <motion.form 
              key={currentStep}
              onSubmit={currentStep === OFFBOARDING_STEPS.length ? handleFinalCompletion : handleNextStep} 
              className="p-8"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
            >
              <div className="mb-8">
                <div className="flex items-center text-xl font-bold font-poppins text-gray-800">
                  {CurrentStepIcon && <CurrentStepIcon className="h-6 w-6 mr-2 text-red-500" />}
                  {OFFBOARDING_STEPS.find(s => s.id === currentStep)?.name}
                </div>
                <p className="text-sm text-gray-500 mt-1 font-roboto">Step {currentStep} of {OFFBOARDING_STEPS.length}: Please provide the required information below.</p>
              </div>

              <div className="min-h-[300px] mb-8">
                {/* Step content will be rendered here based on currentStep */}
                {currentStep === 1 && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormInput 
                        id="resignationDate" 
                        label="Date of Resignation Submission" 
                        type="date" 
                        value={formData.resignationDate} 
                        onChange={handleChange} 
                        required 
                        disabled={isManagerApproved && !completionMessage?.includes('rejected')}
                      />
                      <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-700 flex items-start border border-gray-300">
                        <CalendarDaysIcon className="h-5 w-5 mt-0.5 mr-3 flex-shrink-0 text-red-500" />
                        <p>
                          Your official joining date was: <span className="font-semibold ml-1">{employeeData.joiningDate}</span>. 
                          This dictates your tenure and benefits calculation.
                        </p>
                      </div>
                    </div>

                    <FormInput 
                      id="resignationReason" 
                      label="Reason for Resignation" 
                      type="textarea" 
                      rows={4} 
                      value={formData.resignationReason} 
                      onChange={handleChange} 
                      placeholder="Please provide a professional and detailed reason for your separation from the company. (e.g., Career growth opportunity, personal reasons, etc.)" 
                      required 
                      disabled={isManagerApproved && !completionMessage?.includes('rejected')}
                    />
                    
                    <div className="p-4 bg-red-50 rounded-lg text-sm text-red-700 flex items-center border border-red-300">
                      <ExclamationCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                      <span className='font-semibold'>MANDATORY APPROVAL:</span> Submitting this form initiates the official exit process and requires management and HR approval.
                    </div>
                    
                    {isManagerApproved && !completionMessage?.includes('rejected') && (
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-2">
                          <CheckCircleIcon className="h-5 w-5 text-green-600" />
                          <p className="text-green-800 font-semibold">✅ Step 1 Completed - Manager Approved</p>
                        </div>
                        <p className="text-green-700 text-sm mt-1">Your resignation details have been approved and are now locked.</p>
                      </div>
                    )}
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
                      <div className="flex flex-col">
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          Notice Period
                        </label>
                        <FormInput
                          id="noticePeriodDays"
                          type="select"
                          value={formData.noticePeriodDays}
                          onChange={handleChange}
                          disabled={isManagerApproved && !completionMessage?.includes('rejected')}
                        >
                          {[15, 30, 60, 90].map(days => (
                            <option key={days} value={days}>
                              {days} Days
                            </option>
                          ))}
                        </FormInput>
                        <p className="mt-1 text-xs text-gray-500">Select your intended notice period</p>
                      </div>
                      
                      <div className="flex flex-col">
                        <FormInput 
                          id="requestedLastWorkingDay" 
                          label="Last Working Day (Auto-calculated)" 
                          type="date" 
                          value={formData.requestedLastWorkingDay || calculateExitDates(formData.resignationDate, formData.noticePeriodDays).lastWorkingDay || ''} 
                          onChange={handleChange} 
                          disabled={true}
                          required 
                        />
                        <p className="mt-1 text-xs text-gray-500">Automatically calculated based on notice period</p>
                      </div>

                      <div className="flex flex-col h-full">
                        <div className="p-4 rounded-lg bg-gray-50 border border-gray-300 flex-1 flex flex-col justify-between">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-700">Line Manager</h4>
                            <p className="text-md font-bold text-gray-800 mt-1">{employeeData.managerName}</p>
                          </div>
                          <p className="text-xs text-gray-500 mt-3">Your manager must approve the final exit date.</p>
                        </div>
                      </div>
                    </div>
                    
                    <FormInput 
                      id="managerNotes" 
                      label="Notes for Manager/HR (Regarding LWD)" 
                      type="textarea" 
                      rows={2} 
                      value={formData.managerNotes} 
                      onChange={handleChange} 
                      placeholder="e.g., Requesting an early release date due to X reason..." 
                      required={false} 
                      disabled={isManagerApproved && !completionMessage?.includes('rejected')}
                    />

                    {/* Submit Resignation Button */}
                    {!isResignationSubmitted && (
                      <div className="pt-6 border-t">
                        <motion.button
                          type="button"
                          onClick={handleSubmitResignation}
                          disabled={!formData.resignationDate || !formData.resignationReason || loading}
                          className={`inline-flex items-center px-6 py-3 text-base font-semibold rounded-lg shadow-md transition-all ${
                            !formData.resignationDate || !formData.resignationReason || loading
                              ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                              : 'text-white bg-red-600 hover:bg-red-700 focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                          }`}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <HandRaisedIcon className="h-5 w-5 mr-2" />
                          {loading ? 'Submitting...' : 'Submit Resignation'}
                        </motion.button>
                      </div>
                    )}

                    {/* Manager Approval Status */}
                    {isResignationSubmitted && (
                      <div className={`p-4 rounded-lg border ${
                        isManagerApproved 
                          ? 'bg-green-50 border-green-200 text-green-800'
                          : 'bg-amber-50 border-amber-200 text-amber-800'
                      }`}>
                        <div className="flex items-center gap-2">
                          {isManagerApproved ? (
                            <CheckCircleIcon className="h-5 w-5 text-green-600" />
                          ) : (
                            <ClockIcon className="h-5 w-5 text-amber-600" />
                          )}
                          <p className="font-semibold">
                            {isManagerApproved 
                              ? '✅ Step 2 Completed - Manager Approved - You can proceed to next steps'
                              : 'Awaiting Manager Approval - Please wait for your manager to review your resignation in TeamRequests'
                            }
                          </p>
                        </div>
                        {isManagerApproved && (
                          <p className="text-green-700 text-sm mt-1">Your notice period and exit details have been approved and are now locked.</p>
                        )}
                      </div>
                    )}
                    
                    {/* Show completion message if set */}
                    {completionMessage && (
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-blue-800 font-medium">{completionMessage}</p>
                      </div>
                    )}
                  </div>
                )}

                {currentStep === 3 && (
                  <div className="space-y-6">
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="text-sm font-semibold text-blue-800 mb-2">Knowledge Transfer Requirements</h4>
                      <p className="text-sm text-blue-700">Please ensure all your current projects and responsibilities are properly documented and handed over.</p>
                    </div>
                    
                    <FormInput 
                      id="handoverDocumentLink" 
                      label="Handover Document Link" 
                      type="url" 
                      value={formData.handoverDocumentLink} 
                      onChange={handleChange} 
                      placeholder="https://docs.google.com/document/d/..." 
                      required 
                    />
                    
                    <FormInput 
                      id="handoverRecipient" 
                      label="Handover Recipient (Employee Code)" 
                      type="text" 
                      value={formData.handoverRecipient} 
                      onChange={handleChange} 
                      placeholder="EMP-001" 
                      required 
                    />
                    
                    <FormInput 
                      id="pendingTasks" 
                      label="Pending Tasks & Projects" 
                      type="textarea" 
                      rows={4} 
                      value={formData.pendingTasks} 
                      onChange={handleChange} 
                      placeholder="List all pending tasks, ongoing projects, and important information that needs to be transferred..." 
                      required 
                    />
                  </div>
                )}

                {currentStep === 4 && (
                  <div className="space-y-6">
                    <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <h4 className="text-sm font-semibold text-orange-800 mb-2">Company Asset Return</h4>
                      <p className="text-sm text-orange-700">Please confirm the return status of all company assets assigned to you.</p>
                    </div>
                    
                    <div className="space-y-4">
                      {companyAssets.map((asset) => (
                        <div key={asset.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                          <div>
                            <h5 className="font-semibold text-gray-900">{asset.name}</h5>
                            <p className="text-sm text-gray-600">Serial: {asset.serial}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <AssetStatusTag status={asset.status} />
                            <button
                              onClick={() => handleAssetStatusChange(asset.id, asset.status === 'Confirmed' ? 'Pending Return' : 'Confirmed')}
                              className={`px-3 py-1 text-xs font-semibold rounded-md transition ${
                                asset.status === 'Confirmed'
                                  ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                  : 'bg-green-600 text-white hover:bg-green-700'
                              }`}
                            >
                              {asset.status === 'Confirmed' ? 'Mark Pending' : 'Confirm Return'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <FormInput 
                      id="assetReturnNotes" 
                      label="Asset Return Notes" 
                      type="textarea" 
                      rows={3} 
                      value={formData.assetReturnNotes} 
                      onChange={handleChange} 
                      placeholder="Any additional notes about asset returns..." 
                      required={false} 
                    />
                  </div>
                )}

                {currentStep === 5 && (
                  <div className="space-y-6">
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <h4 className="text-sm font-semibold text-green-800 mb-2">Final Documentation</h4>
                      <p className="text-sm text-green-700">Please provide your preferred method for receiving your relieving letter and experience certificate.</p>
                    </div>
                    
                    <FormInput 
                      id="documentDeliveryMethod" 
                      label="Personal Email for Document Delivery" 
                      type="email" 
                      value={formData.documentDeliveryMethod} 
                      onChange={handleChange} 
                      placeholder="your.personal@email.com" 
                      required 
                    />
                    
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <h5 className="font-semibold text-gray-800 mb-2">Exit Process Summary</h5>
                      <div className="space-y-2 text-sm text-gray-700">
                        <p><strong>Resignation Date:</strong> {formData.resignationDate}</p>
                        <p><strong>Last Working Day:</strong> {formData.requestedLastWorkingDay}</p>
                        <p><strong>Notice Period:</strong> {formData.noticePeriodDays} days</p>
                        <p><strong>Assets Status:</strong> {companyAssets.every(a => a.status === 'Confirmed') ? 'All Confirmed' : 'Pending Confirmation'}</p>
                      </div>
                    </div>
                    
                    {/* Final Submit Button */}
                    <div className="pt-6 border-t">
                      <motion.button
                        type="button"
                        onClick={handleFinalCompletion}
                        disabled={!isStepValid || loading}
                        className={`w-full inline-flex items-center justify-center px-6 py-4 text-lg font-bold rounded-lg shadow-lg transition-all ${
                          !isStepValid || loading
                            ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                            : 'text-white bg-green-600 hover:bg-green-700 focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <ClipboardDocumentCheckIcon className="h-6 w-6 mr-3" />
                        {loading ? 'Submitting Final Documentation...' : 'Submit Final Documentation & Complete Exit Process'}
                      </motion.button>
                    </div>
                  </div>
                )}
              </div>

              {/* Navigation Footer */}
              <div className="flex justify-between border-t pt-6">
                <motion.button
                  type="button"
                  onClick={() => setCurrentStep(prev => prev - 1)}
                  disabled={currentStep === 1}
                  className={`inline-flex items-center px-6 py-2.5 border border-gray-300 text-base font-semibold rounded-lg text-gray-700 bg-white transition-colors font-roboto ${
                    currentStep === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <ChevronLeftIcon className="h-5 w-5 mr-2" />
                  Previous
                </motion.button>

                {currentStep < OFFBOARDING_STEPS.length && currentStep !== 5 && (
                  <motion.button
                    type="submit"
                    disabled={!isStepValid || loading}
                    className={`inline-flex items-center px-6 py-2.5 text-base font-semibold rounded-lg shadow-md transition-all font-roboto ${
                      !isStepValid || loading
                        ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                        : 'text-white bg-red-600 hover:bg-red-700 focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {loading ? 'Processing...' : 'Next Step'} <ChevronRightIcon className="h-5 w-5 ml-2" />
                  </motion.button>
                )}
              </div>
            </motion.form>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-10 text-center flex flex-col items-center justify-center min-h-[500px]"
            >
              <ClipboardDocumentCheckIcon className="h-20 w-20 text-green-500 mb-6 animate-pulse" />
              <h2 className="text-3xl font-extrabold font-poppins text-gray-900 mb-4">
                Exit Formalities Acknowledged
              </h2>
              <p className="text-lg text-gray-600 max-w-xl font-roboto">
                {completionMessage}
              </p>
              <div className="mt-8">
                <button
                  onClick={() => alert("Navigating to Employee Portal Home (Simulated)")}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-semibold rounded-lg shadow-md text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
                >
                  Return to Portal
                </button>
              </div>
            </motion.div>
          )}

          {/* Simulated Manager Approval Button - REMOVED */}
        </div>
      </div>
    </div>
  );
};

export default EmployeeOffboarding;